import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Header.module.css';
import { useAuth } from '../../context/AuthContext';

// Import logo as a module for better build handling
import PlatefullAILogo from '../../images/PlatefullAILogoNB.svg';

interface HeaderProps {
  user?: {
    id: string;
    name?: string;
    email?: string;
    picture?: string;
  } | null;
  onLogin?: () => void;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onLogin, onLogout }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.headerContent}>
        {/* Left section: Mobile menu button + Logo */}
        <div className={styles.leftSection}>
          <button
            className={styles.menuButton}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          >
            <span className={`${styles.menuBar} ${isMobileMenuOpen ? styles.menuBarTopOpen : ''}`} />
            <span className={`${styles.menuBar} ${isMobileMenuOpen ? styles.menuBarMiddleOpen : ''}`} />
            <span className={`${styles.menuBar} ${isMobileMenuOpen ? styles.menuBarBottomOpen : ''}`} />
          </button>

          <Link to="/" className={styles.logo}>
            <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
          </Link>
        </div>

        {/* Navigation Links (desktop) */}
        <nav className={styles.navigation}>
          <Link to="/" className={styles.navLink}>Home</Link>
          <Link to="/recipes" className={styles.navLink}>Recipes</Link>
        </nav>

        {/* Right side buttons */}
        <div className={styles.rightSection}>
          {user ? (
            <div className={styles.userSection}>
              <div className={styles.userAvatar}>
                {user.picture ? (
                  <img src={user.picture} alt={user.name || 'User'} className={styles.avatarImage} />
                ) : (
                  <div className={styles.avatarPlaceholder}>
                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
              </div>
              
              <button 
                className={styles.settingsButton} 
                onClick={() => navigate('/settings')}
                title="Digital ID Card"
                aria-label="Open Digital ID Card"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                  <path d="M6 8h2v2H6zm4 0h2v2h-2zm4 0h2v2h-2z"/>
                </svg>
              </button>
            </div>
          ) : (
            <div className={styles.authButtons}>
              <Link to="/portal/landing" className={styles.businessLink}>
                Business
              </Link>
              <button className={styles.loginButton} onClick={() => navigate('/onboard')}>
                <svg className={styles.userIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile navigation drawer */}
      <div
        className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.open : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <nav className={`${styles.mobileNav} ${isMobileMenuOpen ? styles.open : ''}`}>
        <div className={styles.mobileNavHeader}>
          <span className={styles.mobileTitle}>Menu</span>
          <button
            className={styles.mobileClose}
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>
        <div className={styles.mobileNavLinks} onClick={() => setIsMobileMenuOpen(false)}>
          <Link to="/" className={styles.mobileNavLink}>Home</Link>
          <Link to="/recipes" className={styles.mobileNavLink}>Recipes</Link>
          {!user && (
            <Link to="/portal/landing" className={styles.mobileNavLink}>Business</Link>
          )}
        </div>
        <div className={styles.mobileActions}>
          {user ? (
            <div className={styles.mobileUserActions}>
              <button 
                className={styles.mobileSettingsButton} 
                onClick={() => { setIsMobileMenuOpen(false); navigate('/settings'); }}
              >
                Settings
              </button>
              <button 
                className={styles.mobileLogoutButton} 
                onClick={() => { onLogout && onLogout(); setIsMobileMenuOpen(false); }}
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className={styles.mobileAuthActions}>
              <Link 
                to="/portal/landing" 
                className={styles.mobileBusinessLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Business
              </Link>
              <button 
                className={styles.mobileLoginButton} 
                onClick={() => { setIsMobileMenuOpen(false); navigate('/onboard'); }}
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </nav>

    </header>
  );
};

export default Header; 